import { useCallback, useEffect, useState } from 'react';

// Shapes returned by the auth service via the /api/clinic/* BFF proxy. Only the
// fields the Notendur UI renders are typed; unknown extras are ignored.
export interface ClinicMemberGrant {
  app: string;
  role: string;
  active: boolean;
}

export interface ClinicMember {
  account_id: string;
  name: string;
  kennitala?: string | null;
  app_grants: ClinicMemberGrant[];
}

export interface ClinicInvite {
  id: string;
  email: string;
  status: string;
  role_name: string;
  app_slug: string;
  expires_at?: string | null;
  invite_url?: string | null;
  email_sent?: boolean;
}

export interface InviteResult {
  email_sent: boolean;
  invite_url?: string | null;
}

interface ClinicMembersState {
  members: ClinicMember[];
  invites: ClinicInvite[];
  isLoading: boolean;
  error: string | null;
}

const jsonHeaders = { Accept: 'application/json' } as const;

async function readError(response: Response, fallback: string): Promise<string> {
  try {
    const body = await response.json();
    if (body && typeof body.detail === 'string') {
      return body.detail;
    }
  } catch {
    // non-JSON error body — fall through
  }
  return fallback;
}

/**
 * Data hook for the Stöðvastillingar → Notendur section. Wraps the five
 * /api/clinic/* BFF routes. The CSRF token is attached automatically by the
 * global fetch interceptor, so state-changing calls need no manual header.
 */
export function useClinicMembers(enabled: boolean) {
  const [state, setState] = useState<ClinicMembersState>({
    members: [],
    invites: [],
    isLoading: enabled,
    error: null,
  });

  const refresh = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const [membersRes, invitesRes] = await Promise.all([
        fetch('/api/clinic/members', { credentials: 'include', headers: jsonHeaders }),
        fetch('/api/clinic/invites', { credentials: 'include', headers: jsonHeaders }),
      ]);
      if (!membersRes.ok) {
        throw new Error(await readError(membersRes, 'Ekki tókst að sækja notendur'));
      }
      if (!invitesRes.ok) {
        throw new Error(await readError(invitesRes, 'Ekki tókst að sækja boð'));
      }
      const membersBody = await membersRes.json();
      const invitesBody = await invitesRes.json();
      setState({
        members: Array.isArray(membersBody?.members) ? membersBody.members : [],
        invites: Array.isArray(invitesBody?.invites) ? invitesBody.invites : [],
        isLoading: false,
        error: null,
      });
    } catch (err) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Villa kom upp',
      }));
    }
  }, []);

  useEffect(() => {
    if (enabled) {
      void refresh();
    }
  }, [enabled, refresh]);

  const invite = useCallback(
    async (email: string): Promise<InviteResult> => {
      const response = await fetch('/api/clinic/invites', {
        method: 'POST',
        credentials: 'include',
        headers: { ...jsonHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!response.ok) {
        throw new Error(await readError(response, 'Ekki tókst að senda boð'));
      }
      const body = await response.json();
      await refresh();
      return { email_sent: Boolean(body?.email_sent), invite_url: body?.invite_url ?? null };
    },
    [refresh]
  );

  const removeMember = useCallback(
    async (accountId: string): Promise<void> => {
      const response = await fetch(`/api/clinic/members/${encodeURIComponent(accountId)}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: jsonHeaders,
      });
      if (!response.ok) {
        throw new Error(await readError(response, 'Ekki tókst að fjarlægja notanda'));
      }
      await refresh();
    },
    [refresh]
  );

  const revokeInvite = useCallback(
    async (inviteId: string): Promise<void> => {
      const response = await fetch(`/api/clinic/invites/${encodeURIComponent(inviteId)}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: jsonHeaders,
      });
      if (!response.ok) {
        throw new Error(await readError(response, 'Ekki tókst að afturkalla boð'));
      }
      await refresh();
    },
    [refresh]
  );

  return {
    members: state.members,
    invites: state.invites,
    isLoading: state.isLoading,
    error: state.error,
    refresh,
    invite,
    removeMember,
    revokeInvite,
  };
}
